import { useState, useEffect } from 'react';
import { useEnquiryModal } from '@/hooks/useEnquiryModal';

const quantityOptions = [
  'Daily Requirement',
  'Weekly Requirement',
  'Monthly Requirement',
  'Bulk Order',
];

export default function EnquiryModal() {
  const { isOpen, closeEnquiry, productName } = useEnquiryModal();

  const [formData, setFormData] = useState({
    fullName: '',
    companyName: '',
    phone: '',
    email: '',
    location: '',
    requiredVegetables: false,
    requiredFruits: false,
    requiredBoth: false,
    productDetails: '',
    expectedQuantity: '',
    specialInstructions: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        fullName: '',
        companyName: '',
        phone: '',
        email: '',
        location: '',
        requiredVegetables: false,
        requiredFruits: false,
        requiredBoth: false,
        productDetails: '',
        expectedQuantity: '',
        specialInstructions: '',
      });
      setErrors({});
      setSubmitting(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeEnquiry();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEsc);
      return () => window.removeEventListener('keydown', handleEsc);
    }
  }, [isOpen, closeEnquiry]);

  if (!isOpen) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!formData.fullName.trim()) errs.fullName = 'Full name is required';
    if (!formData.phone.trim()) errs.phone = 'Phone number is required';
    if (!formData.location.trim()) errs.location = 'Location is required';
    if (!formData.requiredVegetables && !formData.requiredFruits && !formData.requiredBoth) {
      errs.requiredProducts = 'Please select at least one product type';
    }
    if (!formData.expectedQuantity) errs.expectedQuantity = 'Please select expected quantity';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const getRequiredProductsLabel = (): string => {
    if (formData.requiredBoth) return 'Both (Vegetables & Fruits)';
    const parts: string[] = [];
    if (formData.requiredVegetables) parts.push('Vegetables');
    if (formData.requiredFruits) parts.push('Fruits');
    return parts.join(', ') || 'Not specified';
  };

  const buildWhatsAppMessage = (): string => {
    const lines = [
      'Hello Bassi Enterprises,',
      '',
      'I want fresh produce supply.',
      '',
      'Enquiry Product:',
      productName,
      '',
      'Name:',
      formData.fullName.trim(),
    ];

    if (formData.companyName.trim()) {
      lines.push('', 'Company:', formData.companyName.trim());
    }

    lines.push('', 'Phone:', formData.phone.trim());

    if (formData.email.trim()) {
      lines.push('', 'Email:', formData.email.trim());
    }

    lines.push('', 'Location:', formData.location.trim());
    lines.push('', 'Required Products:', getRequiredProductsLabel());

    if (formData.productDetails.trim()) {
      lines.push('', 'Product Details:', formData.productDetails.trim());
    }

    lines.push('', 'Quantity:', formData.expectedQuantity);

    if (formData.specialInstructions.trim()) {
      lines.push('', 'Message:', formData.specialInstructions.trim());
    }

    return lines.join('\n');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);

    const message = buildWhatsAppMessage();
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/919084756574?text=${encodedMessage}`;

    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    closeEnquiry();
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) closeEnquiry();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6"
      onClick={handleOverlayClick}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>

      {/* Modal Card */}
      <div className="relative z-10 w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-background-50 rounded-2xl shadow-2xl animate-[fadeIn_0.2s_ease-out]">
        {/* Close Button */}
        <button
          onClick={closeEnquiry}
          className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 text-foreground-500 hover:text-foreground-900 transition-all duration-200 cursor-pointer z-10"
          aria-label="Close"
        >
          <i className="ri-close-line text-lg"></i>
        </button>

        {/* Header */}
        <div className="px-6 md:px-8 pt-8 pb-2">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-1">
            Request Bulk Supply Quote
          </h2>
          <p className="text-foreground-500 text-sm">
            Fill your details and our team will contact you.
          </p>
        </div>

        {/* Enquiry Product Banner */}
        <div className="mx-6 md:mx-8 mt-4 px-4 py-3 bg-primary-50 border border-primary-200 rounded-xl flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
            <i className="ri-shopping-basket-2-line text-primary-600 text-sm"></i>
          </div>
          <div>
            <p className="text-foreground-500 text-[11px] uppercase tracking-wide font-medium">You&apos;re enquiring about</p>
            <p className="text-foreground-900 text-sm font-semibold">{productName}</p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 md:px-8 py-6 space-y-5" noValidate>
          {/* Full Name + Company */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-foreground-700 mb-1.5">
                Full Name <span className="text-accent-500">*</span>
              </label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="Enter your full name"
                className={`w-full px-4 py-3 text-sm border rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 transition-all ${
                  errors.fullName ? 'border-accent-500 focus:ring-accent-200' : 'border-background-200 focus:border-primary-400 focus:ring-primary-100'
                }`}
              />
              {errors.fullName && <p className="text-accent-600 text-xs mt-1">{errors.fullName}</p>}
            </div>
            <div>
              <label htmlFor="companyName" className="block text-sm font-medium text-foreground-700 mb-1.5">
                Company Name
              </label>
              <input
                type="text"
                id="companyName"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                placeholder="Enter company / business name"
                className="w-full px-4 py-3 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
              />
            </div>
          </div>

          {/* Phone + Email */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-foreground-700 mb-1.5">
                Phone / WhatsApp Number <span className="text-accent-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-sm font-medium">+91</span>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Enter WhatsApp number"
                  className={`w-full pl-14 pr-4 py-3 text-sm border rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 transition-all ${
                    errors.phone ? 'border-accent-500 focus:ring-accent-200' : 'border-background-200 focus:border-primary-400 focus:ring-primary-100'
                  }`}
                />
              </div>
              {errors.phone && <p className="text-accent-600 text-xs mt-1">{errors.phone}</p>}
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter email address"
                className="w-full px-4 py-3 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
              />
            </div>
          </div>

          {/* Location */}
          <div>
            <label htmlFor="location" className="block text-sm font-medium text-foreground-700 mb-1.5">
              Location / Delivery Address <span className="text-accent-500">*</span>
            </label>
            <input
              type="text"
              id="location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="Enter your location or delivery address"
              className={`w-full px-4 py-3 text-sm border rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 transition-all ${
                errors.location ? 'border-accent-500 focus:ring-accent-200' : 'border-background-200 focus:border-primary-400 focus:ring-primary-100'
              }`}
            />
            {errors.location && <p className="text-accent-600 text-xs mt-1">{errors.location}</p>}
          </div>

          {/* Required Products */}
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-2">
              Required Products <span className="text-accent-500">*</span>
            </label>
            <div className="flex flex-wrap gap-3">
              <label className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border cursor-pointer transition-all duration-200 ${
                formData.requiredVegetables
                  ? 'bg-primary-50 border-primary-400 text-primary-700'
                  : 'bg-background-50 border-background-200 text-foreground-600 hover:border-background-300'
              }`}>
                <input
                  type="checkbox"
                  name="requiredVegetables"
                  checked={formData.requiredVegetables}
                  onChange={handleChange}
                  className="sr-only"
                />
                <i className={`text-sm ${formData.requiredVegetables ? 'ri-checkbox-circle-fill text-primary-500' : 'ri-checkbox-blank-circle-line text-foreground-300'}`}></i>
                <span className="text-sm font-medium whitespace-nowrap">Vegetables</span>
              </label>
              <label className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border cursor-pointer transition-all duration-200 ${
                formData.requiredFruits
                  ? 'bg-primary-50 border-primary-400 text-primary-700'
                  : 'bg-background-50 border-background-200 text-foreground-600 hover:border-background-300'
              }`}>
                <input
                  type="checkbox"
                  name="requiredFruits"
                  checked={formData.requiredFruits}
                  onChange={handleChange}
                  className="sr-only"
                />
                <i className={`text-sm ${formData.requiredFruits ? 'ri-checkbox-circle-fill text-primary-500' : 'ri-checkbox-blank-circle-line text-foreground-300'}`}></i>
                <span className="text-sm font-medium whitespace-nowrap">Fruits</span>
              </label>
              <label className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border cursor-pointer transition-all duration-200 ${
                formData.requiredBoth
                  ? 'bg-primary-50 border-primary-400 text-primary-700'
                  : 'bg-background-50 border-background-200 text-foreground-600 hover:border-background-300'
              }`}>
                <input
                  type="checkbox"
                  name="requiredBoth"
                  checked={formData.requiredBoth}
                  onChange={handleChange}
                  className="sr-only"
                />
                <i className={`text-sm ${formData.requiredBoth ? 'ri-checkbox-circle-fill text-primary-500' : 'ri-checkbox-blank-circle-line text-foreground-300'}`}></i>
                <span className="text-sm font-medium whitespace-nowrap">Both</span>
              </label>
            </div>
            {errors.requiredProducts && <p className="text-accent-600 text-xs mt-1.5">{errors.requiredProducts}</p>}
          </div>

          {/* Product Details + Expected Quantity */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="productDetails" className="block text-sm font-medium text-foreground-700 mb-1.5">
                Product Details
              </label>
              <textarea
                id="productDetails"
                name="productDetails"
                value={formData.productDetails}
                onChange={handleChange}
                rows={3}
                maxLength={500}
                placeholder="Enter products required"
                className="w-full px-4 py-3 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
              ></textarea>
            </div>
            <div>
              <label htmlFor="expectedQuantity" className="block text-sm font-medium text-foreground-700 mb-1.5">
                Expected Quantity <span className="text-accent-500">*</span>
              </label>
              <select
                id="expectedQuantity"
                name="expectedQuantity"
                value={formData.expectedQuantity}
                onChange={handleChange}
                className={`w-full px-4 py-3 text-sm border rounded-lg bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 transition-all appearance-none cursor-pointer ${
                  errors.expectedQuantity ? 'border-accent-500 focus:ring-accent-200' : 'border-background-200 focus:border-primary-400 focus:ring-primary-100'
                }`}
                style={{ backgroundImage: 'none' }}
              >
                <option value="" disabled className="text-foreground-400">Select expected quantity</option>
                {quantityOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
              {errors.expectedQuantity && <p className="text-accent-600 text-xs mt-1">{errors.expectedQuantity}</p>}
            </div>
          </div>

          {/* Special Instructions */}
          <div>
            <label htmlFor="specialInstructions" className="block text-sm font-medium text-foreground-700 mb-1.5">
              Special Instructions
            </label>
            <textarea
              id="specialInstructions"
              name="specialInstructions"
              value={formData.specialInstructions}
              onChange={handleChange}
              rows={3}
              maxLength={500}
              placeholder="Any additional requirements"
              className="w-full px-4 py-3 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
            ></textarea>
            <p className="text-foreground-400 text-xs mt-1">{formData.specialInstructions.length}/500</p>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2.5 px-8 py-3.5 bg-[#25D366] hover:bg-[#20BD5A] text-background-50 font-semibold rounded-full text-base transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-[#25D366]/25"
          >
            <i className="ri-whatsapp-fill text-xl"></i>
            Continue to WhatsApp
          </button>

          <p className="text-center text-foreground-400 text-xs">
            After submitting, you will be redirected to WhatsApp with your enquiry details.
          </p>
        </form>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}